import { User, LogOut, MessageSquare, Eye, Shield, ShieldCheck, Package, Tag, LayoutGrid, CheckCircle2 } from 'lucide-react';
import { MVP_FEATURES } from '@/lib/mvpFeatures';
import { useIdVerification } from '@/hooks/useIdVerification';
import { VerifyIdSheet } from '@/components/verification/VerifyIdSheet';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { EditableAvatar } from '@/components/profile/EditableAvatar';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { useUnreadCount } from '@/hooks/useNotifications';
import { useInquiriesUnreadCount } from '@/hooks/useInquiries';
import { useMyActiveEbayListingsCount } from '@/hooks/useEbayListings';
import { useState } from 'react';
import { useChatStore } from '@/store/chatStore';
import { useMarkSoldStore } from '@/store/markSoldStore';
import { useLocation } from 'react-router-dom';
import { circleShape } from '@/lib/shapeStyles';
import { cn } from '@/lib/utils';

import { useNavigateWithFrom } from '@/lib/navWithFrom';
import { StorefrontContactSheet } from '@/components/contact/StorefrontContactSheet';
import { RETAIL_MODE, OWNER_USER_ID, OWNER_DISPLAY_NAME } from '@/lib/retailMode';

export const UserMenu = () => {
	const { user, signOut } = useAuth();
	const { profile } = useProfile();
	const { isAdmin } = useIsAdmin();
	const { data: notifUnread = 0 } = useUnreadCount();
	const { data: inquiryUnread = 0 } = useInquiriesUnreadCount();
	const { data: activeEbayCount = 0 } = useMyActiveEbayListingsCount();
	const hasUnread = (notifUnread + inquiryUnread) > 0;
	const chatUnread = notifUnread + inquiryUnread;
	const username = profile?.username;
	const [open, setOpen] = useState(false);
	const [verifyOpen, setVerifyOpen] = useState(false);
	const [contactOpen, setContactOpen] = useState(false);
	const openChat = useChatStore((s) => s.open);
	const openMarkSold = useMarkSoldStore((s) => s.openMarkSold);
	const { data: idVer } = useIdVerification();
	const navigate = useNavigateWithFrom();

	// Temporarily hidden menu rows. Flip to true to re-enable.
	const SHOW_VERIFY_ID_ROW = false;
	const SHOW_CONTACT_ROW = false;
	const location = useLocation();

	const isActive = (path: string) => location.pathname === path;
	const go = (path: string) => () => { setOpen(false); navigate(path); };

	const itemCls = (path: string) =>
		cn('cursor-pointer', isActive(path) && 'text-[hsl(50,100%,68%)]');

	const CountBadge = ({ n }: { n: number }) =>
		n > 0 ? (
			<span className="ml-2 inline-flex h-4 min-w-4 px-1 rounded-full bg-active-gold text-[10px] font-medium text-background items-center justify-center">
				{n > 9 ? '9+' : n}
			</span>
		) : null;

	return (
		<DropdownMenu open={open} onOpenChange={setOpen}>
			<DropdownMenuTrigger asChild>
				<Button
					variant="ghost"
					className={cn('relative h-10 w-10 hover:bg-transparent', circleShape)}
					aria-label="Open user menu"
					data-profile-anchor=""
				>
					<EditableAvatar
						avatarUrl={profile?.avatar_url}
						username={profile?.username}
						email={user?.email}
						size="md"
						editable={false}
					/>
					{hasUnread && (
						<span
							className="absolute top-0 right-0 h-2.5 w-2.5 rounded-full bg-active-gold ring-2 ring-background"
							aria-hidden="true"
						/>
					)}
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent
				className="w-60 backdrop-blur-md bg-black/30 border-white/[0.08] shadow-[inset_0_1px_1px_rgba(255,255,255,0.03)]"
				align="end"
				forceMount
				data-user-menu-dropdown=""
			>
				{/* Header: email only, no "Account" label */}
				<div className="px-2 py-2">
					<p className="text-xs leading-normal text-muted-foreground/80 truncate">{user?.email}</p>
				</div>
				<DropdownMenuSeparator className="bg-white/[0.08]" />

				{/* Notifications + inquiries now live in the bottom-right chat panel.
				    Avatar unread dot still aggregates both signals. */}
				<DropdownMenuItem
					className="cursor-pointer"
					onClick={() => {
						setOpen(false);
						openChat();
					}}
				>
					<span className="mr-2 inline-flex h-4 w-4 items-center justify-center shrink-0"><MessageSquare className="h-4 w-4" /></span>
					<span>Messages</span>
					<CountBadge n={chatUnread} />
				</DropdownMenuItem>

				{/* SELLING — page navigation. Candidate to migrate to a top nav row
				    when one exists; until then the avatar doubles as the desktop gateway. */}
				<DropdownMenuItem className={itemCls('/inventory')} onClick={go('/inventory')}>
					<span className="mr-2 inline-flex h-4 w-4 items-center justify-center shrink-0"><Package className="h-4 w-4" /></span>
					<span>Inventory</span>
				</DropdownMenuItem>
				{username && (
					<DropdownMenuItem className={itemCls('/preview')} onClick={go('/preview')}>
						<span className="mr-2 inline-flex h-4 w-4 items-center justify-center shrink-0"><Eye className="h-4 w-4" /></span>
						<span>Preview Storefront</span>
					</DropdownMenuItem>
				)}
				<DropdownMenuItem
					className="cursor-pointer"
					onClick={() => { setOpen(false); openMarkSold(); }}
				>
					<span className="mr-2 inline-flex h-4 w-4 items-center justify-center shrink-0"><CheckCircle2 className="h-4 w-4" /></span>
					<span>Mark Cards as Sold</span>
				</DropdownMenuItem>
				{MVP_FEATURES.menus && (
					<DropdownMenuItem className={itemCls('/menus')} onClick={go('/menus')}>
						<span className="mr-2 inline-flex h-4 w-4 items-center justify-center shrink-0"><LayoutGrid className="h-4 w-4" /></span>
						<span>Menus &amp; Shortcuts</span>
					</DropdownMenuItem>
				)}



				{activeEbayCount > 0 && (
					<DropdownMenuItem className={itemCls('/ebay/listings')} onClick={go('/ebay/listings')}>
						<span className="mr-2 inline-flex h-4 w-4 items-center justify-center shrink-0"><Tag className="h-4 w-4" /></span>
						<span>My eBay Listings</span>
					</DropdownMenuItem>
				)}

				<DropdownMenuSeparator className="bg-white/[0.08]" />

				{/* ACCOUNT — account-scoped rows. Mirrors the mobile @me sheet
				    (BottomNavMeSheet) so identity feels consistent across surfaces. */}

			{SHOW_VERIFY_ID_ROW && user && idVer?.status !== 'approved' && (
				<DropdownMenuItem
					className="cursor-pointer"
					onClick={() => {
						setOpen(false);
						if (idVer?.status !== 'pending') setVerifyOpen(true);
					}}
				>
					<span className="mr-2 inline-flex h-4 w-4 items-center justify-center shrink-0">
						<Shield className={cn('h-4 w-4', idVer?.status !== 'pending' && 'text-active-gold')} />
					</span>
					<span className={cn(idVer?.status !== 'pending' && 'text-active-gold')}>
						{idVer?.status === 'pending'
							? 'ID under review'
							: idVer?.status === 'rejected'
								? 'ID rejected — try again'
								: 'Verify your ID'}
					</span>
					{idVer?.status !== 'pending' && (
						<span className="ml-auto text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-active-gold/15 text-active-gold border border-active-gold/30">
							Recommended
						</span>
					)}
				</DropdownMenuItem>
			)}
			{SHOW_VERIFY_ID_ROW && user && idVer?.status === 'approved' && (
				<DropdownMenuItem className="cursor-default opacity-90" onSelect={(e) => e.preventDefault()}>
					<span className="mr-2 inline-flex h-4 w-4 items-center justify-center shrink-0">
						<ShieldCheck className="h-4 w-4 text-active-gold" />
					</span>
					<span>ID Verified</span>
				</DropdownMenuItem>
			)}

				<DropdownMenuItem
					className={cn('cursor-pointer', (isActive('/settings') || isActive('/settings')) && 'text-[hsl(50,100%,68%)]')}
					onClick={go('/settings')}
				>
					<span className="mr-2 inline-flex h-4 w-4 items-center justify-center shrink-0"><User className="h-4 w-4" /></span>
					<span>Profile / Settings</span>
				</DropdownMenuItem>
				{SHOW_CONTACT_ROW && (
					<DropdownMenuItem
						className="cursor-pointer"
						onClick={() => {
							setOpen(false);
							if (RETAIL_MODE) {
								setTimeout(() => setContactOpen(true), 100);
							} else {
								useChatStore.getState().openTeamThread();
							}
						}}
					>
						<span className="mr-2 inline-flex h-4 w-4 items-center justify-center shrink-0"><MessageSquare className="h-4 w-4" /></span>
						<span>Contact</span>
					</DropdownMenuItem>
				)}
				{isAdmin && (
					<DropdownMenuItem className={itemCls('/admin')} onClick={go('/admin')}>
						<span className="mr-2 inline-flex h-4 w-4 items-center justify-center shrink-0"><Shield className="h-4 w-4" /></span>
						<span>Admin</span>
					</DropdownMenuItem>
				)}

				<DropdownMenuSeparator className="bg-white/[0.08]" />

				<DropdownMenuItem
					className="cursor-pointer text-red-400 focus:text-red-300 focus:bg-red-500/10"
					onClick={() => signOut()}
				>
					<span className="mr-2 inline-flex h-4 w-4 items-center justify-center shrink-0"><LogOut className="h-4 w-4" /></span>
					<span>Sign Out</span>
				</DropdownMenuItem>
			</DropdownMenuContent>
			<VerifyIdSheet open={verifyOpen} onOpenChange={setVerifyOpen} />
			{RETAIL_MODE && (
				<StorefrontContactSheet
					open={contactOpen}
					onOpenChange={setContactOpen}
					sellerUserId={OWNER_USER_ID}
					sellerDisplayName={OWNER_DISPLAY_NAME}
				/>
			)}
		</DropdownMenu>
	);
};
